
import { useState, useEffect } from 'react';

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export default function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    setPreferences(allAccepted);
    saveCookiePreferences(allAccepted);
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    setPreferences(onlyNecessary);
    saveCookiePreferences(onlyNecessary);
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences(preferences);
    setIsVisible(false);
    setShowPreferences(false);
  };

  const saveCookiePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      timestamp: new Date().toISOString(),
      preferences: prefs,
    }));
  };

  const handlePreferenceChange = (type: keyof CookiePreferences) => {
    if (type === 'necessary') return; // Necessary cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-[#f28c1a] shadow-lg z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-[#f28c1a] rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-shield-check-line text-white text-sm"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-[#221a32] mb-2">
                    Utilizamos cookies para melhorar sua experiência
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">
                    Este site utiliza cookies para oferecer uma experiência personalizada, analisar o tráfego e 
                    melhorar nossos serviços. Ao continuar navegando, você concorda com nossa política de cookies.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <button
                onClick={() => setShowPreferences(true)}
                className="px-4 py-2 text-[#221a32] border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Gerenciar Preferências
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 text-[#221a32] border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Recusar Todos
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 bg-[#f28c1a] text-white rounded-lg hover:bg-[#e07a15] transition-colors whitespace-nowrap cursor-pointer"
              >
                Aceitar Todos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-[#221a32]">
                  Preferências de Cookies
                </h2>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-[#221a32] hover:text-[#f28c1a] cursor-pointer"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>

              <div className="space-y-6">
                <p className="text-gray-600">
                  Personalize suas preferências de cookies. Você pode alterar essas configurações a qualquer momento.
                </p>

                {/* Necessary Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-[#5a6ab0] rounded-full flex items-center justify-center">
                        <i className="ri-shield-check-line text-white text-xs"></i>
                      </div>
                      <h3 className="font-semibold text-[#221a32]">Cookies Necessários</h3>
                    </div>
                    <div className="bg-gray-200 rounded-full p-1">
                      <div className="w-4 h-4 bg-[#5a6ab0] rounded-full"></div>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600">
                    Essenciais para o funcionamento básico do site. Não podem ser desabilitados.
                  </p>
                </div>

                {/* Analytics Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-[#4bb5ab] rounded-full flex items-center justify-center">
                        <i className="ri-bar-chart-line text-white text-xs"></i>
                      </div>
                      <h3 className="font-semibold text-[#221a32]">Cookies de Análise</h3>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('analytics')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        preferences.analytics ? 'bg-[#4bb5ab]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.analytics ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    Nos ajudam a entender como os visitantes interagem com o site coletando informações anonimamente.
                  </p>
                </div>

                {/* Marketing Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-[#f28c1a] rounded-full flex items-center justify-center">
                        <i className="ri-megaphone-line text-white text-xs"></i>
                      </div>
                      <h3 className="font-semibold text-[#221a32]">Cookies de Marketing</h3>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('marketing')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        preferences.marketing ? 'bg-[#f28c1a]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.marketing ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    Utilizados para exibir anúncios relevantes e medir a eficácia de campanhas publicitárias.
                  </p>
                </div>

                {/* Functional Cookies */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-6 h-6 bg-[#ea5836] rounded-full flex items-center justify-center">
                        <i className="ri-settings-line text-white text-xs"></i>
                      </div>
                      <h3 className="font-semibold text-[#221a32]">Cookies Funcionais</h3>
                    </div>
                    <button
                      onClick={() => handlePreferenceChange('functional')}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors cursor-pointer ${
                        preferences.functional ? 'bg-[#ea5836]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          preferences.functional ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600">
                    Permitem funcionalidades aprimoradas e personalização, como lembrar suas preferências.
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <button
                  onClick={handleRejectAll}
                  className="flex-1 px-4 py-3 text-[#221a32] border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Recusar Todos
                </button>
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-4 py-3 bg-[#f28c1a] text-white rounded-lg hover:bg-[#e07a15] transition-colors whitespace-nowrap cursor-pointer"
                >
                  Salvar Preferências
                </button>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 text-center">
                  Para mais informações sobre como utilizamos cookies, consulte nossa{' '}
                  <a href="/politica-privacidade" className="text-[#f28c1a] hover:underline">
                    Política de Privacidade
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
